import { FaCalendarAlt, FaUserFriends, FaExchangeAlt } from "react-icons/fa";
import { useState, useContext, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CalendarDropdown from "../ui/CalendarDropdown";
import AirportSelect, { AirportOption } from "../ui/AirportSelect";
import { format, startOfToday } from "date-fns";
import { FaPlaneDeparture, FaPlaneArrival } from "react-icons/fa";
import { CurrencyContext } from "../common/CurrencyContext";
import { formatPassengerCounts } from "../../utils/passengerUtils";
import { useScrollPosition } from "../../hooks/useScrollPosition";
import { useSearch } from "../../context/SearchContext";

const API_URL = process.env.REACT_APP_API_URL || "";

interface FlightSearchBarProps {
  showDirectOnly: boolean;
  setShowDirectOnly: React.Dispatch<React.SetStateAction<boolean>>;
  from?: string;
  to?: string;
  selectedFrom?: AirportOption | null; // <-- Add this
  selectedTo?: AirportOption | null; // <-- Add this
  departureDate?: string | Date;
  returnDate?: string | Date;
  selectedClass?: string;
  tripType?: "oneway" | "roundtrip" | "multicity";
  passengerCounts?: {
    ADT: number;
    CHD: number;
    INF: number;
  };
  isEditMode?: boolean;
  onToggleEditMode?: () => void;
  initialSegments?: MultiCitySegment[];
}

interface MultiCitySegment {
  from: AirportOption | null;
  to: AirportOption | null;
  departureDate: string | undefined;
}

const FlightSearchBar = ({
  showDirectOnly,
  setShowDirectOnly,
  from,
  to,
  selectedFrom: propSelectedFrom,
  selectedTo: propSelectedTo,
  departureDate: propDepartureDate,
  returnDate: propReturnDate,
  selectedClass: propSelectedClass,
  tripType: propTripType,
  passengerCounts,
  isEditMode = true, // true for home page, false for results page
  onToggleEditMode,
  initialSegments: propInitialSegments,
}: FlightSearchBarProps) => {
  // Dropdown control state - use string | null to support dynamic multi-city dropdown identifiers
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const departureRef = useRef<HTMLDivElement>(null);
  const returnRef = useRef<HTMLDivElement>(null);
  const travellerRef = useRef<HTMLDivElement>(null);
  const classRef = useRef<HTMLDivElement>(null);
  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);
  // Multi-city refs for dropdowns (3 per segment: from, to, date)
  const multiCityRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { isScrolled } = useScrollPosition();
  const [isStacked, setIsStacked] = useState(false);

  useEffect(() => {
    if (window.innerWidth < 640) {
      setIsStacked(isScrolled);
    }
  }, [isScrolled]);

  const [departureDate, setDepartureDate] = useState<string | undefined>(
    typeof propDepartureDate === "string"
      ? propDepartureDate
      : propDepartureDate
        ? propDepartureDate.toISOString().split("T")[0]
        : undefined
  );
  const [returnDate, setReturnDate] = useState<string | undefined>(
    typeof propReturnDate === "string"
      ? propReturnDate
      : propReturnDate
        ? propReturnDate.toISOString().split("T")[0]
        : undefined
  );
  const [selectedClass, setSelectedClass] = useState(
    propSelectedClass || "Economy"
  );
  const [loading, setLoading] = useState(false);
  const [alternateDates, setAlternateDates] = useState(false);
  const [alternateAirports, setAlternateAirports] = useState(false);
  const navigate = useNavigate();

  // Cabin class display names and their codes as per Sabre mapping
  const cabinClassOptions = [
    { label: "Economy", code: "Y" },
    { label: "Premium Economy", code: "S" },
    { label: "Business", code: "C" },
    { label: "Premium Business", code: "J" },
    { label: "First class", code: "F" },
    { label: "Premium First", code: "P" },
  ];

  const handleSelect = (option: string) => {
    setSelectedClass(option);
    setOpenDropdown(null);
  };

  const handleIncrement = (type: keyof typeof passengerCount) => {
    setPassengerCount((prev) => ({ ...prev, [type]: prev[type] + 1 }));
  };

  const handleDecrement = (type: keyof typeof passengerCount) => {
    setPassengerCount((prev) => ({
      ...prev,
      [type]: Math.max(prev[type] - 1, 0),
    }));
  };

  // Airport selection state and suggestions
  const [fromSuggestions, setFromSuggestions] = useState<AirportOption[]>([]);
  const [toSuggestions, setToSuggestions] = useState<AirportOption[]>([]);
  const [selectedFrom, setSelectedFrom] = useState<AirportOption | null>(
    propSelectedFrom || (from ? { name: from, code: "", city: from } : null)
  );
  const [selectedTo, setSelectedTo] = useState<AirportOption | null>(
    propSelectedTo || (to ? { name: to, code: "", city: to } : null)
  );

  // Initialize query state with selected airport names
  const [fromQuery, setFromQuery] = useState(selectedFrom?.name || from || "");
  const [toQuery, setToQuery] = useState(selectedTo?.name || to || "");

  // Fetch airport suggestions for FROM
  useEffect(() => {
    if (fromQuery.length < 3) {
      setFromSuggestions([]);
      return;
    }
    fetch(
      `${API_URL}/geo-autocomplete?query=${encodeURIComponent(
        fromQuery
      )}&category=AIR&limit=3`
    )
      .then((res) => res.json())
      .then((data) => {
        setFromSuggestions(Array.isArray(data) ? data : []);
      })
      .catch(() => setFromSuggestions([]));
  }, [fromQuery]);

  // Fetch airport suggestions for TO
  useEffect(() => {
    if (toQuery.length < 3) {
      setToSuggestions([]);
      return;
    }
    fetch(
      `${API_URL}/geo-autocomplete?query=${encodeURIComponent(
        toQuery
      )}&category=AIR&limit=3`
    )
      .then((res) => res.json())
      .then((data) => {
        setToSuggestions(Array.isArray(data) ? data : []);
      })
      .catch(() => setToSuggestions([]));
  }, [toQuery]);

  const [tripType, setTripType] = useState<
    "oneway" | "roundtrip" | "multicity"
  >(propTripType || "roundtrip");

  // Multi-city segments state (initialized from prop when on results page)
  const [segments, setSegments] = useState<MultiCitySegment[]>([
    { from: null, to: null, departureDate: undefined },
    { from: null, to: null, departureDate: undefined },
  ]);

  // Sync segments and multi-city queries when initialSegments is passed (e.g. from results page)
  useEffect(() => {
    if (
      propInitialSegments &&
      Array.isArray(propInitialSegments) &&
      propInitialSegments.length >= 2
    ) {
      setSegments(propInitialSegments);
      setMultiCityQueries((prev) => {
        const next = { ...prev };
        propInitialSegments.forEach((seg, i) => {
          next[`${i}`] = {
            from: seg.from?.name ?? "",
            to: seg.to?.name ?? "",
          };
        });
        return next;
      });
    }
  }, [propInitialSegments]);

  // Multi-city segment-specific state for airport search
  const [multiCityQueries, setMultiCityQueries] = useState<{
    [key: string]: { from: string; to: string };
  }>({});

  const [multiCitySuggestions, setMultiCitySuggestions] = useState<{
    [key: string]: { from: AirportOption[]; to: AirportOption[] };
  }>({});

  const [passengerCount, setPassengerCount] = useState({
    ADT: passengerCounts?.ADT ?? 1,
    CHD: passengerCounts?.CHD ?? 0,
    INF: passengerCounts?.INF ?? 0,
  });

  // Validation errors state for inline display
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  // Function to fetch suggestions for a specific multi-city segment
  const fetchMultiCityAirportSuggestions = async (
    query: string,
    segmentIndex: number,
    type: "from" | "to"
  ) => {
    if (query.length < 3) {
      setMultiCitySuggestions((prev) => ({
        ...prev,
        [`${segmentIndex}`]: {
          ...prev[`${segmentIndex}`],
          [type]: [],
        },
      }));
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/geo-autocomplete?query=${encodeURIComponent(
          query
        )}&category=AIR&limit=3`
      );
      const suggestions = await res.json();

      setMultiCitySuggestions((prev) => ({
        ...prev,
        [`${segmentIndex}`]: {
          ...prev[`${segmentIndex}`],
          [type]: Array.isArray(suggestions) ? suggestions : [],
        },
      }));
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setMultiCitySuggestions((prev) => ({
        ...prev,
        [`${segmentIndex}`]: {
          ...prev[`${segmentIndex}`],
          [type]: [],
        },
      }));
    }
  };

  const handleMultiCityQueryChange = (
    query: string,
    segmentIndex: number,
    type: "from" | "to"
  ) => {
    setMultiCityQueries((prev) => ({
      ...prev,
      [`${segmentIndex}`]: {
        ...prev[`${segmentIndex}`],
        [type]: query,
      },
    }));
    fetchMultiCityAirportSuggestions(query, segmentIndex, type);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const standardRefs = [
        departureRef,
        returnRef,
        travellerRef,
        classRef,
        fromRef,
        toRef,
      ];

      // Check standard refs
      const clickedInStandardRef = standardRefs.some((ref) =>
        ref.current?.contains(event.target as Node)
      );

      // Check multi-city refs (they are direct elements, not RefObjects)
      const clickedInMultiCityRef = multiCityRefs.current.some((element) =>
        element?.contains(event.target as Node)
      );

      if (!clickedInStandardRef && !clickedInMultiCityRef) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const resolveFlightType = (
    tripType: "oneway" | "roundtrip",
    adults: number,
    children: number,
    infants: number
  ): string => {
    const isReturn = tripType === "roundtrip";
    if (adults && children && infants)
      return isReturn ? "ADTCHILDINFRETURN" : "ADTCHILDINF1WAY";
    if (adults && children) return isReturn ? "ADTCHILDRETURN" : "ADTCHILD1WAY";
    if (adults && infants) return isReturn ? "ADTINFRETURN" : "ADTINF1WAY";
    return isReturn ? "ADTRETURN" : "ADT1WAY";
  };

  // Find the code for the selected class
  const classOfServiceCode =
    cabinClassOptions.find((c) => c.label === selectedClass)?.code || "Y";

  const { currency } = useContext(CurrencyContext);

  // Multi-city validation function with field-specific error tracking
  const validateMultiCitySegments = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (segments.length < 2) {
      errors.general = "Please add at least 2 flights";
      setValidationErrors(errors);
      return false;
    }

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];

      // Check if all fields are filled
      if (!segment.from) {
        errors[`segment-${i}-from`] = "Please select origin airport";
      }
      if (!segment.to) {
        errors[`segment-${i}-to`] = "Please select destination airport";
      }
      if (!segment.departureDate) {
        errors[`segment-${i}-date`] = "Please select departure date";
      }

      // Check if origin and destination are different
      if (segment.from && segment.to && segment.from.code === segment.to.code) {
        errors[`segment-${i}-route`] =
          "Origin and destination cannot be the same";
      }

      // Check chronological order for better performance and logical flow
      if (i > 0) {
        const currentDate = segment.departureDate;
        const prevDate = segments[i - 1].departureDate;

        if (
          currentDate &&
          prevDate &&
          new Date(currentDate) < new Date(prevDate)
        ) {
          errors[`segment-${i}-date`] =
            "Date must be on or after previous flight";
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setValidationErrors({}); // Clear previous errors

      // Validation for multi-city
      if (tripType === "multicity") {
        const isValid = validateMultiCitySegments();
        if (!isValid) {
          setLoading(false);
          // Scroll to first error
          setTimeout(() => {
            const firstErrorKey = Object.keys(validationErrors)[0];
            if (firstErrorKey) {
              const errorElement = document.querySelector(
                `[data-error-key="${firstErrorKey}"]`
              );
              errorElement?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 100);
          return;
        }
      } else {
        // Validation for one-way/roundtrip
        const errors: { [key: string]: string } = {};

        if (!selectedFrom) errors.from = "Please select origin airport";
        if (!selectedTo) errors.to = "Please select destination airport";
        if (!departureDate) errors.departure = "Please select departure date";
        if (tripType === "roundtrip" && !returnDate) {
          errors.return = "Please select return date";
        }

        if (Object.keys(errors).length > 0) {
          setValidationErrors(errors);
          setLoading(false);
          return;
        }
      }

      let payload: any;

      if (tripType === "multicity") {
        // Multi-city payload
        payload = {
          data: {
            flightType: "MULTICITY",
            segments: segments.map((seg) => ({
              from: seg.from!.code,
              to: seg.to!.code,
              departureDate: format(new Date(seg.departureDate!), "yyyy-MM-dd"),
            })),
            passengerQuantities: {
              ADT: passengerCount.ADT,
              CHD: passengerCount.CHD,
              INF: passengerCount.INF,
            },
          },
          classOfService: classOfServiceCode,
          currency: currency || "AED",
          alternateDates: alternateDates || undefined,
          alternateAirports: alternateAirports || undefined,
        };
      } else {
        // One-way/Roundtrip payload
        const flightType = resolveFlightType(
          tripType as "oneway" | "roundtrip",
          passengerCount.ADT,
          passengerCount.CHD,
          passengerCount.INF
        );
        payload = {
          data: {
            flightType,
            departureDate: departureDate
              ? format(departureDate, "yyyy-MM-dd")
              : undefined,
            returnDate:
              tripType === "roundtrip"
                ? returnDate
                  ? format(returnDate, "yyyy-MM-dd")
                  : undefined
                : undefined,
            from: selectedFrom?.code,
            to: selectedTo?.code,
            passengerQuantities: {
              ADT: passengerCount.ADT,
              CHD: passengerCount.CHD,
              INF: passengerCount.INF,
            },
          },
          classOfService: classOfServiceCode,
          currency: currency || "AED",
          alternateDates: alternateDates || undefined,
          alternateAirports: alternateAirports || undefined,
        };
      }
      const res = await fetch(`${process.env.REACT_APP_API_URL}/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!res.ok) {
        const msg = result?.errors?.[0]?.message ?? result?.error ?? "Request failed";
        throw new Error(msg);
      }

      // Reset edit mode to false after successful search
      if (isInResultsPage && onToggleEditMode) {
        onToggleEditMode(); // This will set isEditMode to false in parent
      }

      navigate("/results", {
        state: {
          flights: result,
          tripType: tripType,
          from:
            tripType === "multicity"
              ? segments[0]?.from?.name
              : selectedFrom?.name,
          to:
            tripType === "multicity"
              ? segments[segments.length - 1]?.to?.name
              : selectedTo?.name,
          selectedFrom:
            tripType === "multicity" ? segments[0]?.from : selectedFrom,
          selectedTo:
            tripType === "multicity"
              ? segments[segments.length - 1]?.to
              : selectedTo,
          segments: tripType === "multicity" ? segments : undefined,
          passengerCounts: {
            ADT: passengerCount.ADT,
            CHD: passengerCount.CHD,
            INF: passengerCount.INF,
          },
          departureDate:
            tripType === "multicity"
              ? segments[0]?.departureDate
              : departureDate,
          returnDate,
          classOfService: classOfServiceCode,
          selectedClass,
          showDirectOnly,
        },
      });
    } catch (err) {
      console.error("Flight Search Failed:", err);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get today's date in yyyy-mm-dd
  const todayStr = format(startOfToday(), "yyyy-MM-dd");

  // Swap handler
  const handleSwap = () => {
    // Swap selectedFrom and selectedTo
    const tempSelected = selectedFrom;
    setSelectedFrom(selectedTo);
    setSelectedTo(tempSelected);

    // Swap queries
    const tempQuery = fromQuery;
    setFromQuery(toQuery);
    setToQuery(tempQuery);
  };

  const { updateSearchParams } = useSearch();

  // Update the handleSelectFrom function
  const handleSelectFrom = (airport: AirportOption) => {
    setSelectedFrom(airport);
    setFromQuery(airport.name); // Update the query to show selected airport name
    setOpenDropdown(null);
    updateSearchParams({ from: airport.code });
  };

  // Update the handleSelectTo function
  const handleSelectTo = (airport: AirportOption) => {
    setSelectedTo(airport);
    setToQuery(airport.name); // Update the query to show selected airport name
    setOpenDropdown(null);
    updateSearchParams({ to: airport.code });
  };

  const isInResultsPage = onToggleEditMode !== undefined;

  const handleSearchOrToggle = () => {
    if (isInResultsPage) {
      if (isEditMode) {
        handleSearch(); // This will reset edit mode after search
      } else {
        onToggleEditMode?.(); // Enable edit mode
      }
    } else {
      handleSearch();
    }
  };

  return (
    <div className="bg-primary text-black rounded-xl p-4 sm:p-5 md:p-7 shadow-md text-sm text-left space-y-4 sm:space-y-2 ">
      {/* Trip Type Selection - Make radio buttons more touch-friendly on mobile */}
      <div className="flex gap-4 sm:gap-6 mb-4 text-white font-sans">
        <label className="flex items-center gap-2 cursor-pointer">
          One Way
          <input
            type="radio"
            name="tripType"
            value="oneway"
            checked={tripType === "oneway"}
            onChange={() => setTripType("oneway")}
            className="appearance-none w-4 h-4 rounded-full border-2 border-white checked:bg-[#D9D9D9]"
          />
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          Round Trip
          <input
            type="radio"
            name="tripType"
            value="roundtrip"
            checked={tripType === "roundtrip"}
            onChange={() => setTripType("roundtrip")}
            className="appearance-none w-4 h-4 rounded-full  border-2 border-white checked:bg-[#D9D9D9]"
          />
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          Multi-City
          <input
            type="radio"
            name="tripType"
            value="multicity"
            checked={tripType === "multicity"}
            onChange={() => {
              setTripType("multicity");
              // Initialize with 2 segments if not already set
              if (segments.length < 2) {
                setSegments([
                  { from: null, to: null, departureDate: undefined },
                  { from: null, to: null, departureDate: undefined },
                ]);
              }
            }}
            className="appearance-none w-4 h-4 rounded-full border-2 border-white checked:bg-[#D9D9D9]"
          />
        </label>
      </div>

      {/* Search Fields Grid - Responsive layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        {/* FROM and TO Fields - Only for one-way and roundtrip */}
        {tripType !== "multicity" && (
          <>
            {/* FROM Field */}
            <div className="relative w-full" ref={fromRef}>
              <div
                className={`bg-white rounded-lg px-3 py-2.5 w-full shadow-md ${isEditMode
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-60"
                  }`}
                onClick={() =>
                  isEditMode &&
                  setOpenDropdown(openDropdown === "from" ? null : "from")
                }
              >
                <p className="text-black flex items-center justify-between text-subheading font-sora">
                  From
                  <span className="text-primary w-[21.01px] h-[21.01px]">
                    <FaPlaneDeparture className="text-primary w-[21.01px] h-[21.01px]" />
                  </span>
                </p>
                <p className="text-base text-heading truncate">
                  {selectedFrom?.name || "Select airport"}
                </p>
              </div>
              {/* Swap Button - bottom center on mobile, right edge on larger screens */}
              <button
                type="button"
                aria-label="Swap locations"
                className={`
              absolute z-10 shadow-md p-1.5 rounded-full bg-primary
              left-1/2 -translate-x-1/2 -bottom-6
              sm:left-auto sm:translate-x-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:right-[-28px]
              ${isEditMode ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}
            `}
                onClick={isEditMode ? handleSwap : undefined}
                disabled={!isEditMode}
              >
                <FaExchangeAlt className="text-white w-3 h-3 sm:w-5 sm:h-5" />
              </button>
              {openDropdown === "from" && isEditMode && (
                <AirportSelect
                  suggestions={fromSuggestions}
                  query={fromQuery}
                  setQuery={setFromQuery}
                  onSelect={handleSelectFrom}
                  isOpen={openDropdown === "from"}
                  setIsOpen={(open) => setOpenDropdown(open ? "from" : null)}
                  dropdownRef={fromRef}
                />
              )}
            </div>

            {/* TO Field */}
            <div className="relative w-full" ref={toRef}>
              <div
                className={`bg-white rounded-lg px-3 py-2.5 w-full shadow-md ${isEditMode
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-60"
                  }`}
                onClick={() =>
                  isEditMode &&
                  setOpenDropdown(openDropdown === "to" ? null : "to")
                }
              >
                <p className="text-black flex items-center justify-between text-subheading font-sora">
                  To
                  <span className="text-primary w-[21.01px] h-[21.01px]">
                    <FaPlaneArrival className="text-primary w-[21.01px] h-[21.01px]" />
                  </span>
                </p>
                <p className="text-base text-heading truncate">
                  {selectedTo?.name || "Select airport"}
                </p>
              </div>
              {openDropdown === "to" && isEditMode && (
                <AirportSelect
                  suggestions={toSuggestions}
                  query={toQuery}
                  setQuery={setToQuery}
                  onSelect={handleSelectTo}
                  isOpen={openDropdown === "to"}
                  setIsOpen={(open) => setOpenDropdown(open ? "to" : null)}
                  dropdownRef={toRef}
                />
              )}
            </div>
          </>
        )}

        {/* Multi-City Flights */}
        {tripType === "multicity" && (
          <div className="col-span-full space-y-4">
            {/* Compact View for Results Page (non-edit mode) */}
            {isInResultsPage && !isEditMode ? (
              <div className="bg-white bg-opacity-10 rounded-lg p-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {segments.map((segment, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 bg-white bg-opacity-20 rounded-md px-2 py-1">
                          <span className="text-white text-xs font-medium">
                            {segment.from?.code || segment.from?.city || "---"}
                          </span>
                          <span className="text-white text-xs">→</span>
                          <span className="text-white text-xs font-medium">
                            {segment.to?.code || segment.to?.city || "---"}
                          </span>
                          {segment.departureDate && (
                            <span className="text-white text-xs opacity-80 ml-1">
                              (
                              {format(
                                new Date(segment.departureDate),
                                "dd MMM"
                              )}
                              )
                            </span>
                          )}
                        </div>
                        {index < segments.length - 1 && (
                          <span className="text-white text-xs opacity-50">
                            •
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <span className="text-white text-xs opacity-70">
                    {segments.length}{" "}
                    {segments.length === 1 ? "Flight" : "Flights"}
                  </span>
                </div>
              </div>
            ) : (
              <>
                {/* Full Edit View */}
                <div className="text-white text-base font-semibold mb-3">
                  Plan Your Journey
                </div>

                {/* General error message - Amber theme */}
                {validationErrors.general && (
                  <div className="bg-amber-500 bg-opacity-20 border-2 border-amber-400 text-white px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <span className="text-amber-300 text-lg">⚠</span>
                    <span>{validationErrors.general}</span>
                  </div>
                )}

                {segments.map((segment, index) => (
                  <div
                    key={index}
                    className="bg-white bg-opacity-10 rounded-xl p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-white text-sm font-medium">
                        Flight {index + 1}
                      </div>
                      {segments.length > 2 && (
                        <button
                          onClick={() => {
                            if (isEditMode) {
                              const newSegments = segments.filter(
                                (_, i) => i !== index
                              );
                              setSegments(newSegments);
                              // Clean up errors, queries and suggestions
                              const newErrors = { ...validationErrors };
                              Object.keys(newErrors).forEach((key) => {
                                if (key.startsWith(`segment-${index}-`)) {
                                  delete newErrors[key];
                                }
                              });
                              setValidationErrors(newErrors);

                              const newQueries = { ...multiCityQueries };
                              const newSuggestions = {
                                ...multiCitySuggestions,
                              };
                              delete newQueries[`${index}`];
                              delete newSuggestions[`${index}`];
                              setMultiCityQueries(newQueries);
                              setMultiCitySuggestions(newSuggestions);
                            }
                          }}
                          disabled={!isEditMode}
                          className={`text-white text-xs px-3 py-1 rounded-lg border border-white hover:bg-white hover:text-primary transition ${!isEditMode ? "opacity-60 cursor-not-allowed" : ""
                            }`}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {/* Route-level error - Amber theme */}
                    {validationErrors[`segment-${index}-route`] && (
                      <div className="bg-amber-500 bg-opacity-20 border border-amber-400 text-white px-3 py-2 rounded text-xs flex items-center gap-2">
                        <span className="text-amber-300">⚠</span>
                        <span>
                          {validationErrors[`segment-${index}-route`]}
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* From */}
                      <div
                        className="relative"
                        ref={(el) => (multiCityRefs.current[index * 3] = el)}
                        data-error-key={`segment-${index}-from`}
                      >
                        <div
                          className={`bg-white rounded-lg px-3 py-2.5 transition-all ${validationErrors[`segment-${index}-from`]
                            ? "ring-2 ring-amber-400 shadow-lg shadow-amber-500/50"
                            : ""
                            } ${isEditMode
                              ? "cursor-pointer hover:shadow-md"
                              : "cursor-not-allowed opacity-60"
                            }`}
                          onClick={() => {
                            if (isEditMode) {
                              setOpenDropdown(`multicity-from-${index}`);
                              // Clear error on interaction
                              const newErrors = { ...validationErrors };
                              delete newErrors[`segment-${index}-from`];
                              setValidationErrors(newErrors);
                            }
                          }}
                        >
                          <p className="text-black text-xs flex items-center justify-between font-sora">
                            From
                            <FaPlaneDeparture className="text-primary w-4 h-4" />
                          </p>
                          <p className="text-base text-heading truncate">
                            {segment.from?.name || "Select airport"}
                          </p>
                        </div>
                        {validationErrors[`segment-${index}-from`] && (
                          <p className="text-amber-300 text-xs mt-1 ml-1 flex items-center gap-1">
                            <span>⚠</span>
                            {validationErrors[`segment-${index}-from`]}
                          </p>
                        )}
                        {openDropdown === `multicity-from-${index}` &&
                          isEditMode && (
                            <AirportSelect
                              suggestions={
                                multiCitySuggestions[`${index}`]?.from || []
                              }
                              query={multiCityQueries[`${index}`]?.from || ""}
                              setQuery={(q) =>
                                handleMultiCityQueryChange(q, index, "from")
                              }
                              onSelect={(airport) => {
                                setSegments((prev) =>
                                  prev.map((seg, i) =>
                                    i === index
                                      ? { ...seg, from: airport }
                                      : seg
                                  )
                                );
                                setOpenDropdown(null);
                                // Clear error
                                const newErrors = { ...validationErrors };
                                delete newErrors[`segment-${index}-from`];
                                delete newErrors[`segment-${index}-route`];
                                setValidationErrors(newErrors);
                              }}
                              isOpen={true}
                              setIsOpen={(open) =>
                                setOpenDropdown(
                                  open ? `multicity-from-${index}` : null
                                )
                              }
                            />
                          )}
                      </div>

                      {/* To */}
                      <div
                        className="relative"
                        ref={(el) =>
                          (multiCityRefs.current[index * 3 + 1] = el)
                        }
                        data-error-key={`segment-${index}-to`}
                      >
                        <div
                          className={`bg-white rounded-lg px-3 py-2.5 transition-all ${validationErrors[`segment-${index}-to`]
                            ? "ring-2 ring-amber-400 shadow-lg shadow-amber-500/50"
                            : ""
                            } ${isEditMode
                              ? "cursor-pointer hover:shadow-md"
                              : "cursor-not-allowed opacity-60"
                            }`}
                          onClick={() => {
                            if (isEditMode) {
                              setOpenDropdown(`multicity-to-${index}`);
                              const newErrors = { ...validationErrors };
                              delete newErrors[`segment-${index}-to`];
                              setValidationErrors(newErrors);
                            }
                          }}
                        >
                          <p className="text-black text-xs flex items-center justify-between font-sora">
                            To
                            <FaPlaneArrival className="text-primary w-4 h-4" />
                          </p>
                          <p className="text-base text-heading truncate">
                            {segment.to?.name || "Select airport"}
                          </p>
                        </div>
                        {validationErrors[`segment-${index}-to`] && (
                          <p className="text-amber-300 text-xs mt-1 ml-1 flex items-center gap-1">
                            <span>⚠</span>
                            {validationErrors[`segment-${index}-to`]}
                          </p>
                        )}
                        {openDropdown === `multicity-to-${index}` &&
                          isEditMode && (
                            <AirportSelect
                              suggestions={
                                multiCitySuggestions[`${index}`]?.to || []
                              }
                              query={multiCityQueries[`${index}`]?.to || ""}
                              setQuery={(q) =>
                                handleMultiCityQueryChange(q, index, "to")
                              }
                              onSelect={(airport) => {
                                setSegments((prev) =>
                                  prev.map((seg, i) =>
                                    i === index ? { ...seg, to: airport } : seg
                                  )
                                );
                                setOpenDropdown(null);
                                const newErrors = { ...validationErrors };
                                delete newErrors[`segment-${index}-to`];
                                delete newErrors[`segment-${index}-route`];
                                setValidationErrors(newErrors);
                              }}
                              isOpen={true}
                              setIsOpen={(open) =>
                                setOpenDropdown(
                                  open ? `multicity-to-${index}` : null
                                )
                              }
                            />
                          )}
                      </div>

                      {/* Departure Date */}
                      <div
                        className="relative"
                        ref={(el) =>
                          (multiCityRefs.current[index * 3 + 2] = el)
                        }
                        data-error-key={`segment-${index}-date`}
                      >
                        <div
                          className={`bg-white rounded-lg px-3 py-2.5 transition-all ${validationErrors[`segment-${index}-date`]
                            ? "ring-2 ring-amber-400 shadow-lg shadow-amber-500/50"
                            : ""
                            } ${isEditMode
                              ? "cursor-pointer hover:shadow-md"
                              : "cursor-not-allowed opacity-60"
                            }`}
                          onClick={() => {
                            if (isEditMode) {
                              setOpenDropdown(`multicity-date-${index}`);
                              const newErrors = { ...validationErrors };
                              delete newErrors[`segment-${index}-date`];
                              setValidationErrors(newErrors);
                            }
                          }}
                        >
                          <p className="text-black text-xs flex items-center justify-between font-sora">
                            Departure
                            <FaCalendarAlt className="text-primary w-4 h-4" />
                          </p>
                          <p className="text-base text-heading">
                            {segment.departureDate
                              ? format(
                                new Date(segment.departureDate),
                                "dd MMM. yyyy"
                              )
                              : "Select Date"}
                          </p>
                        </div>
                        {validationErrors[`segment-${index}-date`] && (
                          <p className="text-amber-300 text-xs mt-1 ml-1 flex items-center gap-1">
                            <span>⚠</span>
                            {validationErrors[`segment-${index}-date`]}
                          </p>
                        )}
                        {openDropdown === `multicity-date-${index}` &&
                          isEditMode && (
                            <CalendarDropdown
                              months={1}
                              selected={segment.departureDate}
                              onSelect={(date) => {
                                // Debug logging
                                console.log("📅 Date selected:", {
                                  segmentIndex: index,
                                  date: date,
                                  currentSegments: segments,
                                });

                                // Robust functional update
                                setSegments((prevSegments) => {
                                  const updated = prevSegments.map((seg, i) => {
                                    if (i === index) {
                                      console.log(
                                        `✅ Updating segment ${i} with date:`,
                                        date
                                      );
                                      return { ...seg, departureDate: date };
                                    }
                                    return seg;
                                  });
                                  console.log("📊 Updated segments:", updated);
                                  return updated;
                                });

                                setOpenDropdown(null);

                                // Clear error
                                setValidationErrors((prev) => {
                                  const newErrors = { ...prev };
                                  delete newErrors[`segment-${index}-date`];
                                  return newErrors;
                                });
                              }}
                              minDate={
                                index === 0
                                  ? todayStr
                                  : segments[index - 1]?.departureDate ||
                                  todayStr
                              }
                            />
                          )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add Another Flight Button */}
                {segments.length < 6 && isEditMode && (
                  <button
                    onClick={() => {
                      setSegments([
                        ...segments,
                        { from: null, to: null, departureDate: undefined },
                      ]);
                    }}
                    className="text-white text-sm font-medium flex items-center gap-2 hover:text-gray-200 transition"
                  >
                    <span className="text-lg">+</span> Add Another Flight (Max
                    6)
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Date Fields - Responsive width (Only for one-way and roundtrip) */}
        {tripType !== "multicity" && (
          <div
            className={`
          relative w-full 
          transition-all duration-300 ease-in-out
          ${isScrolled && isStacked
                ? "opacity-0 h-0 mt-0 transform translate-y-[-10px]"
                : "opacity-100 block"
              } 
          sm:opacity-100 sm:h-auto sm:mt-0 sm:transform-none
        `}
            ref={departureRef}
          >
            <div
              className={`bg-white rounded-lg px-3 py-2.5 w-full ${isEditMode ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                }`}
              onClick={() =>
                isEditMode &&
                setOpenDropdown(
                  openDropdown === "departure" ? null : "departure"
                )
              }
            >
              <p className="text-black flex items-center justify-between text-subheading font-sora">
                Departure
                <FaCalendarAlt className="text-primary w-[21.01px] h-[21.01px] align-middle" />
              </p>
              <p className="text-base text-heading">
                {departureDate
                  ? format(new Date(departureDate), "dd MMM. yyyy")
                  : "Select Date"}
              </p>
            </div>
            {openDropdown === "departure" && isEditMode && (
              <CalendarDropdown
                months={1}
                selected={departureDate}
                onSelect={(date) => {
                  setDepartureDate(date);
                  // If return date is before new departure, reset return date
                  if (returnDate && date && date > returnDate) {
                    setReturnDate(undefined);
                  }
                  setOpenDropdown(null);
                }}
                minDate={todayStr}
              />
            )}
          </div>
        )}

        {tripType === "roundtrip" && (
          <div
            className={`
            relative w-full 
            transition-all duration-300 ease-in-out
            ${isScrolled && isStacked
                ? "opacity-0 h-0 mt-0 transform translate-y-[-10px]"
                : "opacity-100 block"
              }
            sm:opacity-100 sm:h-auto sm:mt-0 sm:transform-none
          `}
            ref={returnRef}
          >
            <div
              className={`bg-white rounded-lg px-3 py-2.5 w-full ${isEditMode ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                }`}
              onClick={() =>
                isEditMode &&
                setOpenDropdown(openDropdown === "return" ? null : "return")
              }
            >
              <p className="text-black flex items-center justify-between text-subheading font-sora">
                Return
                <FaCalendarAlt className="text-primary w-[21.01px] h-[21.01px] align-middle" />
              </p>
              <p className="text-base text-heading">
                {returnDate
                  ? format(new Date(returnDate), "dd MMM. yyyy")
                  : "Select Date"}
              </p>
            </div>
            {openDropdown === "return" && isEditMode && (
              <CalendarDropdown
                months={1}
                selected={returnDate}
                onSelect={(date) => {
                  setReturnDate(date);
                  setOpenDropdown(null);
                }}
                minDate={departureDate || todayStr}
              />
            )}
          </div>
        )}

        {/* Travellers & Class - Full width on mobile */}
        <div
          className={`
          relative w-full 
          transition-all duration-300 ease-in-out
          ${isScrolled && isStacked
              ? "opacity-0 h-0 mt-0 transform translate-y-[-10px]"
              : "opacity-100 block"
            }
          sm:opacity-100 sm:h-auto sm:mt-0 sm:transform-none
        `}
          ref={travellerRef}
        >
          <div
            className={`bg-white rounded-lg px-4 py-2.5 min-w-[180px] shadow ${isEditMode ? "cursor-pointer" : "cursor-not-allowed opacity-60"
              }`}
            onClick={() =>
              isEditMode &&
              setOpenDropdown(openDropdown === "traveller" ? null : "traveller")
            }
          >
            <p className="text-black flex items-center justify-between text-subheading font-sora">
              Travellers
              <FaUserFriends className="text-primary w-[21.01px] h-[21.01px]" />
            </p>
            <p className="text-base text-heading">
              {formatPassengerCounts(passengerCount)}
            </p>
          </div>
          {openDropdown === "traveller" && isEditMode && (
            <div className="absolute mt-2 bg-[#F5F8FF] rounded-lg p-4 space-y-3 shadow font-sans text-sm z-10 w-full min-w-[250px]">
              <div className="flex justify-between items-center border-b pb-2">
                <p className="font-bold text-black">No. of passengers</p>
                <FaUserFriends className="text-primary" />
              </div>
              {[
                { label: "Adults: (12 and above)", id: "ADT" },
                { label: "Children: (2 - 12 years)", id: "CHD" },
                { label: "Infants: (3 days - 2 years)", id: "INF" },
              ].map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center"
                >
                  <p>{item.label}</p>
                  <div className="flex items-center gap-2">
                    <button
                      className="w-6 h-6 rounded bg-primary text-white text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDecrement(item.id as keyof typeof passengerCount);
                      }}
                    >
                      −
                    </button>
                    <span className="w-6 text-center">
                      {passengerCount[item.id as keyof typeof passengerCount]}
                    </span>
                    <button
                      className="w-6 h-6 rounded bg-primary text-white text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleIncrement(item.id as keyof typeof passengerCount);
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          className={`
          relative w-full 
          transition-all duration-300 ease-in-out
          ${isScrolled && isStacked
              ? "opacity-0 h-0 mt-0  transform translate-y-[-10px]"
              : "opacity-100 block"
            }
          sm:opacity-100 sm:h-auto sm:mt-0 sm:transform-none
        `}
          ref={classRef}
        >
          <div
            className={`bg-white rounded-lg px-3 py-2.5 w-full shadow ${isEditMode ? "cursor-pointer" : "cursor-not-allowed opacity-60"
              }`}
            onClick={() =>
              isEditMode &&
              setOpenDropdown(openDropdown === "class" ? null : "class")
            }
          >
            <p className="text-black flex items-center justify-between font-sora text-subheading">
              Class
              <FaUserFriends className="text-primary w-[21.01px] h-[21.01px]" />
            </p>
            <p className="text-base text-heading truncate">{selectedClass}</p>
          </div>
          {openDropdown === "class" && isEditMode && (
            <div className="absolute mt-2 bg-[#F5F8FF] rounded-lg p-4 shadow space-y-2 font-sans text-sm z-20 w-full min-w-[200px]">
              <div className="flex justify-between items-center border-b pb-2">
                <p className="font-bold text-black">Cabin Class</p>
                <FaUserFriends className="text-primary" />
              </div>
              {cabinClassOptions.map((option) => (
                <div
                  key={option.label}
                  className="flex justify-between items-center hover:bg-white px-2 py-1 rounded-md cursor-pointer transition"
                  onClick={() => handleSelect(option.label)}
                >
                  <p>{option.label}</p>
                  <div
                    className={`w-4 h-4 rounded-sm border ${selectedClass === option.label
                      ? "bg-primary border-primary"
                      : "bg-gray-300 border-gray-400"
                      }`}
                  >
                    {selectedClass === option.label && (
                      <svg
                        width="10"
                        height="10"
                        stroke="white"
                        strokeWidth="5"
                        viewBox="0 0 24 24"
                      >
                        <polyline points="20,6 9,17 4,12" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section - Stack on mobile */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-4">
        {/* <p className="text-xs sm:text-sm text-white max-w-[250px] sm:max-w-none">
          Get {currency} 500 Discount. Promo Code <strong>FIRSTBUILD</strong>{" "}
          for 1st time users.
        </p> */}

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
          <label className="flex items-center gap-2 text-white text-xs sm:text-sm cursor-pointer">
            <input
              type="checkbox"
              className="accent-gray-500 w-4 h-4"
              checked={showDirectOnly}
              onChange={() =>
                isEditMode && setShowDirectOnly((prev: boolean) => !prev)
              }
              disabled={!isEditMode}
            />
            Only Direct Flights
          </label>

          {/* Alternate Date / Airport toggles */}
          {/* <button
            type="button"
            onClick={() => isEditMode && setAlternateDates((p) => !p)}
            disabled={!isEditMode}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition ${
              alternateDates
                ? "bg-white text-primary border-white"
                : "bg-white/10 text-white/80 border-white/20 hover:bg-white/20"
            } ${!isEditMode ? "opacity-60" : ""}`}
          >
            &plusmn; 3 Days
          </button> */}
          <button
            type="button"
            onClick={() => isEditMode && setAlternateAirports((p) => !p)}
            disabled={!isEditMode}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition ${
              alternateAirports
                ? "bg-white text-primary border-white"
                : "bg-white/10 text-white/80 border-white/20 hover:bg-white/20"
            } ${!isEditMode ? "opacity-60" : ""}`}
          >
            Nearby Airports
          </button>

          <button
            onClick={handleSearchOrToggle}
            className="w-full sm:w-auto bg-white text-primary text-sm font-semibold px-6 py-2.5 rounded hover:bg-gray-100 transition flex items-center justify-center min-w-[120px]"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Loading...
              </>
            ) : isInResultsPage ? (
              isEditMode ? (
                "Search Now"
              ) : (
                "Modify Search"
              )
            ) : (
              "Search Now"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlightSearchBar;
